import { useCallback, useReducer } from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';

import { useSportsMyBetsWidget } from '../../../../../../../model';
import type { CashoutBetRequest, UnsuccessfulCashoutBetResponse } from '../types';
import getTranslationForResponseStatus from '../utils/getTranslationForResponseStatus';

import { TXTWCL_SorryX_something_went_wrongX_P_ELL } from './useCashOut.messages.json';
import useRequestCashOut from './useRequestCashOut';

interface State {
  isAwaitingConfirmation: boolean;
  isSubmitting: boolean;
  error?: string;
}

export type CashOutAction =
  | { type: 'SUBMITTING' }
  | { type: 'FAILURE'; payload: string }
  | { type: 'CASHOUT_CONFIRMATION'; payload: boolean }
  | { type: 'CLEAR_ERROR' };

const initialState: State = {
  isAwaitingConfirmation: false,
  isSubmitting: false,
  error: undefined
};

const reducer = (state: State, action: CashOutAction): State => {
  switch (action.type) {
    case 'SUBMITTING':
      return { ...initialState, isSubmitting: true, isAwaitingConfirmation: true };
    case 'FAILURE':
      return { ...initialState, error: action.payload };
    case 'CASHOUT_CONFIRMATION':
      return { ...initialState, error: state.error, isAwaitingConfirmation: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: initialState.error };
    default:
      return state;
  }
};

const useCashOut = () => {
  const { widgetDispatch } = useSportsMyBetsWidget();
  const [state, dispatchAction] = useReducer(reducer, initialState);
  const { formatMessage } = useRegulatoryIntl();
  const requestCashOut = useRequestCashOut();

  const cashOut = useCallback(
    async (
      cashOutBetRequest: CashoutBetRequest,
      trackCashOutError: (responseStatus?: UnsuccessfulCashoutBetResponse['respStatus']) => void
    ) => {
      try {
        dispatchAction({ type: 'SUBMITTING' });

        const { body: response } = await requestCashOut(cashOutBetRequest);

        if (response.respStatus === 'SUCCESS') {
          widgetDispatch.cashedOutSuccessfully({
            betId: cashOutBetRequest.betId.toString(),
            cashedOutAmount: response.cashedOutQuote
          });
        } else {
          widgetDispatch.cashoutQuotesRequested();
          trackCashOutError(response.respStatus);
          dispatchAction({
            type: 'FAILURE',
            payload: formatMessage(getTranslationForResponseStatus(response.respStatus))
          });
        }
      } catch {
        widgetDispatch.cashoutQuotesRequested();
        trackCashOutError();
        dispatchAction({
          type: 'FAILURE',
          payload: formatMessage(TXTWCL_SorryX_something_went_wrongX_P_ELL)
        });
      }
    },
    [requestCashOut, widgetDispatch, formatMessage]
  );

  return { cashOut, dispatchAction, ...state };
};

export default useCashOut;
